import React, { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { BrgButton, ButtonVariant, MEDIA } from '@brg/ui'
import { useModal } from 'core'
import {
  TestAttemptQuestionsSection,
  type TestAttemptQuestion
} from 'shared/pages/testsAdmin/components/TestAttemptQuestionsSection'
import {
  TestAttemptProgressBar,
  TEST_ATTEMPT_FEEDBACK_GREEN,
  TEST_ATTEMPT_FEEDBACK_RED
} from 'shared/pages/testsAdmin/components/TestAttemptProgressBar'

export type LongreadTestResultsPercentTone = 'success' | 'danger'

export type LongreadTestResultsModalArgs = {
  percent: number
  percentTone?: LongreadTestResultsPercentTone
  attemptCurrent: number
  attemptTotal: number
  correctCount: number
  questionTotal: number
  questions?: TestAttemptQuestion[]
  onReturnToLongread?: () => void
  close?: () => void
}

export type { TestAttemptQuestion }

const MODAL_BG = '#FEFEFE'
const GREEN = TEST_ATTEMPT_FEEDBACK_GREEN
const RED = TEST_ATTEMPT_FEEDBACK_RED

const FullscreenPane = styled.div<{ $verticalCenter?: boolean }>`
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  background: ${MODAL_BG};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(p) => (p.$verticalCenter ? 'center' : 'flex-start')};
  padding: 48px 20px 56px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media ${MEDIA.DESKTOP} {
    padding: 72px 80px 96px;
  }
`

const Content = styled.div`
  width: 100%;
  max-width: 1160px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const TitleBand = styled.div`
  margin-top: 8px;
`

const TitleInner = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 34px;
  line-height: 42px;
  color: #151514;

  @media ${MEDIA.LAPTOP} {
    font-size: 23px;
    line-height: 27px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 22px;
    line-height: 28px;
  }
`

const PercentFigure = styled.span<{ $tone: LongreadTestResultsPercentTone }>`
  color: ${(p) => (p.$tone === 'danger' ? RED : GREEN)};
`

const AttemptLine = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 17px;
  line-height: 22px;
  color: #151514;

  @media ${MEDIA.LAPTOP} {
    font-size: 15px;
    line-height: 20px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 13px;
    line-height: 16px;
  }
`

const ButtonsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  justify-content: flex-start;

  @media ${MEDIA.MOBILE} {
    width: 100%;
    flex-direction: column;
  }
`

const ReturnBrgButton = styled(BrgButton)`
  @media ${MEDIA.MOBILE} {
    width: 100%;
  }
`

const QuestionsBelowSummary = styled.div`
  width: 100%;
  max-width: 1160px;
  margin-top: 48px;
  padding-top: 8px;
  border-top: 1px solid #e8e8e8;
`

export const LongreadTestResultsModal: FC<LongreadTestResultsModalArgs> = ({
  percent,
  percentTone = 'success',
  attemptCurrent,
  attemptTotal,
  correctCount,
  questionTotal,
  questions,
  onReturnToLongread,
  close
}) => {
  const { t } = useTranslation()
  const modal = useModal()

  const progressPct = useMemo(() => {
    if (!questionTotal || questionTotal <= 0) {
      return 0
    }
    return (correctCount / questionTotal) * 100
  }, [correctCount, questionTotal])

  const displayPercent = Math.round(percent)

  const finishClose = useCallback(() => {
    if (typeof close === 'function') {
      close()
    }
    modal.hide()
  }, [close, modal])

  const handleReturnToLongread = useCallback(() => {
    onReturnToLongread?.()
    finishClose()
  }, [finishClose, onReturnToLongread])

  const correctSummaryText = t('modal.longread_test_results.correct_line', {
    correct: correctCount,
    total: questionTotal
  })

  const hasQuestions = Boolean(questions && questions.length > 0)

  return (
    <FullscreenPane $verticalCenter={!hasQuestions}>
      <Content>
        <TitleBand>
          <TitleInner>
            <PercentFigure $tone={percentTone}>{displayPercent}%</PercentFigure>{' '}
            {t('modal.longread_test_results.completed_early')}
          </TitleInner>
        </TitleBand>

        <AttemptLine>
          {t('modal.longread_test_results.attempt_line', {
            current: attemptCurrent,
            total: attemptTotal
          })}
        </AttemptLine>

        <TestAttemptProgressBar
          fillPercent={progressPct}
          summary={correctSummaryText}
          fillTone={percentTone}
        />

        <ButtonsRow>
          <ReturnBrgButton variant={ButtonVariant.Filled} onClick={handleReturnToLongread}>
            {t('modal.longread_test_results.return_longread')}
          </ReturnBrgButton>
        </ButtonsRow>
      </Content>

      {hasQuestions && questions ? (
        <QuestionsBelowSummary>
          <TestAttemptQuestionsSection questions={questions} hideSorting />
        </QuestionsBelowSummary>
      ) : null}
    </FullscreenPane>
  )
}
import React, { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { BrgButton, ButtonVariant, MEDIA } from '@brg/ui'
import { useModal } from 'core'
import {
  TestAttemptQuestionsSection,
  type TestAttemptQuestion
} from 'shared/pages/testsAdmin/components/TestAttemptQuestionsSection'
import {
  TestAttemptProgressBar,
  TEST_ATTEMPT_FEEDBACK_GREEN,
  TEST_ATTEMPT_FEEDBACK_RED
} from 'shared/pages/testsAdmin/components/TestAttemptProgressBar'

export type LongreadTestResultsPercentTone = 'success' | 'danger'

export type LongreadTestResultsModalArgs = {
  percent: number
  percentTone?: LongreadTestResultsPercentTone
  attemptCurrent: number
  attemptTotal: number
  correctCount: number
  questionTotal: number
  questions?: TestAttemptQuestion[]
  onReturnToLongread?: () => void
  close?: () => void
}

export type { TestAttemptQuestion }

const MODAL_BG = '#FEFEFE'
const GREEN = TEST_ATTEMPT_FEEDBACK_GREEN
const RED = TEST_ATTEMPT_FEEDBACK_RED

const FullscreenPane = styled.div<{ $verticalCenter?: boolean }>`
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  background: ${MODAL_BG};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(p) => (p.$verticalCenter ? 'center' : 'flex-start')};
  padding: 48px 20px 56px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media ${MEDIA.DESKTOP} {
    padding: 72px 80px 96px;
  }
`

const Content = styled.div`
  width: 100%;
  max-width: 1160px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const TitleBand = styled.div`
  margin-top: 8px;
`

const TitleInner = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 34px;
  line-height: 42px;
  color: #151514;

  @media ${MEDIA.LAPTOP} {
    font-size: 23px;
    line-height: 27px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 22px;
    line-height: 28px;
  }
`

const PercentFigure = styled.span<{ $tone: LongreadTestResultsPercentTone }>`
  color: ${(p) => (p.$tone === 'danger' ? RED : GREEN)};
`

const AttemptLine = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 17px;
  line-height: 22px;
  color: #151514;

  @media ${MEDIA.LAPTOP} {
    font-size: 15px;
    line-height: 20px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 13px;
    line-height: 16px;
  }
`

const ButtonsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  justify-content: flex-start;

  @media ${MEDIA.MOBILE} {
    width: 100%;
    flex-direction: column;
  }
`

const ReturnBrgButton = styled(BrgButton)`
  @media ${MEDIA.MOBILE} {
    width: 100%;
  }
`

const QuestionsBelowSummary = styled.div`
  width: 100%;
  max-width: 1160px;
  margin-top: 48px;
  padding-top: 8px;
  border-top: 1px solid #e8e8e8;
`

export const LongreadTestResultsModal: FC<LongreadTestResultsModalArgs> = ({
  percent,
  percentTone = 'success',
  attemptCurrent,
  attemptTotal,
  correctCount,
  questionTotal,
  questions,
  onReturnToLongread,
  close
}) => {
  const { t } = useTranslation()
  const modal = useModal()

  const progressPct = useMemo(() => {
    if (!questionTotal || questionTotal <= 0) {
      return 0
    }
    return (correctCount / questionTotal) * 100
  }, [correctCount, questionTotal])

  const displayPercent = Math.round(percent)

  const finishClose = useCallback(() => {
    if (typeof close === 'function') {
      close()
    }
    modal.hide()
  }, [close, modal])

  const handleReturnToLongread = useCallback(() => {
    onReturnToLongread?.()
    finishClose()
  }, [finishClose, onReturnToLongread])

  const correctSummaryText = t('modal.longread_test_results.correct_line', {
    correct: correctCount,
    total: questionTotal
  })

  const hasQuestions = Boolean(questions && questions.length > 0)

  return (
    <FullscreenPane $verticalCenter={!hasQuestions}>
      <Content>
        <TitleBand>
          <TitleInner>
            <PercentFigure $tone={percentTone}>{displayPercent}%</PercentFigure>{' '}
            {t('modal.longread_test_results.completed_early')}
          </TitleInner>
        </TitleBand>

        <AttemptLine>
          {t('modal.longread_test_results.attempt_line', {
            current: attemptCurrent,
            total: attemptTotal
          })}
        </AttemptLine>

        <TestAttemptProgressBar
          fillPercent={progressPct}
          summary={correctSummaryText}
          fillTone={percentTone}
        />

        <ButtonsRow>
          <ReturnBrgButton variant={ButtonVariant.Filled} onClick={handleReturnToLongread}>
            {t('modal.longread_test_results.return_longread')}
          </ReturnBrgButton>
        </ButtonsRow>
      </Content>

      {hasQuestions && questions ? (
        <QuestionsBelowSummary>
          <TestAttemptQuestionsSection questions={questions} hideSorting />
        </QuestionsBelowSummary>
      ) : null}
    </FullscreenPane>
  )
}
